<template>
  <div
    ref="doubleCheckPopup"
    tabindex="0"
    class="modal double-check-modal"
    @mousedown="closePopupHandler"
  >
    <div
      class="popup-window double-check-popup"
      @click.stop
      @mousedown.stop
    >
      <PopupHeader
        :title="popupTitle"
        :space-access-check="spaceAccess"
        @close="closePopupHandler"
      />
      <div
        v-if="showItemInfo && showUserInfo"
        class="member-info"
      >
        <div
          v-if="!isInvite"
          class="avatar-image"
        >
          <UserAvatar
            :key="member.id"
            :member="member"
          />
        </div>
        <div class="member-name-wrap">
          <div
            v-if="!isInvite"
            v-member-hover-preview="member"
            class="member-name"
          >
            {{ member.display_name || member.invitee_email }}
          </div>
          <div class="member-email">
            {{ member.email || member.invitee_email }}
          </div>
        </div>
      </div>
      <div class="action-warning">
        <div
          v-if="isNewTag"
          class="merge-warning"
        >
          <div>
            {{ translate('theSpace') }}
            <strong>
              {{ projectsWithTag }}
            </strong>
            <span v-if="lastMergeProject">and</span>
            <strong>
              {{ lastMergeProject }}
            </strong>
            {{ translate('haveSameTags') }}
          </div>
          <div>
            {{ translate('doYouWantToMerge') }}
          </div>
        </div>
        <div
          v-else
          class="whitespace-break-spaces"
        >
          {{ warning }}
        </div>
      </div>
      <div class="buttons-wrap">
        <div class="members-form-buttons-right">
          <ButtonCustom
            class="cancel-button"
            :type="'important'"
            :text="cancelButtonText"
            :variation="'large-fixed'"
            @click="closePopupHandler"
          />
          <ButtonCustom
            class="delete-member-button"
            :class="{
              'discard-comment-button': discardComment || spaceAccess,
            }"
            :type="'primary'"
            :text="buttonName"
            :variation="'large-fixed'"
            @click="deleteMember"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import customIconsMixin from '@/mixins/customIconsMixin';
import UserAvatar from '@/components/widgets/UserAvatar';
import EventBus from '../../utilities/eventBus';
import PopupHeader from '@/components/elements/PopupHeader';

export default {
  name: 'DoubleCheck',
  components: {
    ButtonCustom,
    UserAvatar,
    PopupHeader,
  },
  mixins: [customIconsMixin],
  props: {
    member: {
      type: Object,
      required: true,
    },
    pendingInvites: {
      type: Array,
      required: false,
      default: () => [],
    },
    membersArray: {
      type: Array,
      required: false,
      default: () => [],
    },
    projectsWithSameTags: {
      type: Array,
      required: false,
      default: () => [],
    },
    project: {
      type: Object,
      required: false,
      default: undefined,
    },
    showUserInfo: {
      type: Boolean,
      required: false,
      default: true,
    },
    userData: {
      type: Object,
      required: false,
      default: undefined,
    },
    discardComment: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },
    spaceAccess: {
      type: Boolean,
      required: false,
      default: false,
    },
    spaceType: {
      type: String,
      required: false,
      default: '',
    },
    branchAutomation: {
      type: Object,
      required: false,
      default: null,
    },
    isSection: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    'popupCancelled',
    'setPreviousValue',
    'deleteUnmergedTag',
    'closeDoubleCheckPopup',
    'deleteCommentConfirmed',
    'leaveSpaceConfirmed',
    'mergeTags',
    'deleteTagConfirmed',
    'deleteTemplateConfirmed',
    'deleteBranchWorkflow',
    'deleteSectionConfirmed',
  ],
  data() {
    return {
      membersToAdd: [],
      searchValue: '',
      selectedRoleIndex: 0,
      lastMergeProject: '',
      isOwner: false,
      escFullyPressed: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.getUser;
    },
    getTeamID() {
      return this.$store.getters.getTeamID;
    },
    me() {
      return [this.member.id, this.member.user_id].includes(this.$store.getters.getUserId);
    },
    lastMember() {
      return (
        this.project?.members.length <= 1 &&
        this.project?.members.find((m) => m.user_id === this.member.id)
      );
    },
    projectIsPrivate() {
      return this.project?.is_private;
    },
    isInvite() {
      return this.member.invitee_email;
    },
    isNewTag() {
      return this.member.newTag;
    },
    isTag() {
      return this.member.slug;
    },
    showItemInfo() {
      return !(this.isNewTag || this.isTag);
    },
    projectsWithTag() {
      let projects = [...this.projectsWithSameTags];
      if (projects.length > 1) {
        projects.pop();
      }
      projects = projects.toString().replaceAll(',', ', ');
      return `${projects}`;
    },
    warning() {
      if (this.spaceAccess) {
        return this.spaceType === this.$constants.openSpace
          ? this.translate('changeToOpenSpace')
          : this.translate('changeToPrivateSpace');
      }
      if (this.discardComment) {
        return this.translate('areYouSureYouWantToDiscard');
      }
      if (this.me) {
        if (this.project) {
          if (this.projectIsPrivate) {
            if (this.lastMember) {
              return this.translate('youAreTheOnlyPersonInThisPrivateSpace');
            }
            return this.translate('whenYouLeaveAPrivateSpace');
          }
          return this.translate('youCanRejoinThePublicSpace');
        }
        return this.translate('areYouSureYouWantToLeave');
      }
      if (this.isOwner) {
        return this.translate('areYouSureYouWantToTransferOwnership');
      }
      if (this.isInvite) {
        return this.translate('areYouSureYouWantToRevoke');
      }
      if (this.isNewTag) {
        return `${this.translate('theSpace')} ${this.projectsWithTag}
          ${this.translate('haveSameTags')} ${this.translate('doYouWantToMerge')}`;
      }
      if (this.isTag) {
        return this.translate('youCantUndo');
      }
      if (this.isTemplate) {
        return this.translate('cantUndoneAction');
      }
      if (this.branchAutomation) {
        return this.translate('deleteAutomationConfirmation');
      }
      if (this.isSection) {
        const sectionTitle = this.member.title || this.translate('untitledSection');
        return this.translate('areYouSureYouWantToDelete', {
          resourceTitle: sectionTitle,
          resourceType: this.translate('section'),
        });
      }
      return this.translate('areYouSureYouWantToRemove');
    },
    buttonName() {
      if (this.spaceAccess) {
        return this.spaceType === this.$constants.openSpace
          ? this.translate('changeToOpen')
          : this.translate('changeToPrivate');
      }
      if (this.discardComment) {
        return this.translate('discard');
      }
      if (this.me) {
        if (this.projectIsPrivate && this.lastMember) {
          return `${this.translate('delete')} & ${this.translate('leave')}`;
        }
        return this.translate('leave');
      }
      if (this.isOwner) {
        return this.translate('confirm');
      }
      if (this.isInvite) {
        return this.translate('revoke');
      }
      if (this.isNewTag) {
        return this.translate('merge');
      }
      if (this.isTag) {
        return this.translate('delete');
      }
      if (this.isTemplate) {
        return this.translate('delete');
      }
      if (this.branchAutomation) {
        return this.translate('delete');
      }
      if (this.isSection) {
        return this.translate('delete');
      }
      return this.translate('remove');
    },

    cancelButtonText() {
      return this.translate('cancel');
    },
    popupTitle() {
      if (this.spaceAccess) {
        return this.spaceType === this.$constants.openSpace
          ? this.translate('changeToOpenSpaceTitle')
          : this.translate('changeToPrivateSpaceTitle');
      }
      if (this.discardComment) {
        return this.translate('discardComment');
      }
      if (this.me) {
        if (this.project) {
          if (this.projectIsPrivate) {
            if (this.lastMember) {
              return this.translate('deleteAndLeaveNamedPrivateSpace', {
                name: this.project?.title,
              });
            }
            return this.translate('leaveNamedPrivateSpace', { name: this.project?.title });
          }
          return this.translate('leaveNamedSpace', { name: this.project?.title });
        }
        return this.translate('leaveWorkspace');
      }
      if (this.isOwner) {
        return this.translate('transferOwnership');
      }
      if (this.isInvite) {
        return this.translate('revokeInvite');
      }
      if (this.member.role && !this.me) {
        return this.translate('removeMember');
      }
      if (this.isNewTag) {
        const tagsPlaceholder =
          this.projectsWithSameTags?.length === 1 ? this.translate('tag') : this.translate('tags');
        return `${this.translate('merge')} ${this.projectsWithSameTags?.length} ${this.translate(
          'space'
        ).toLowerCase()}
        ${tagsPlaceholder.toLowerCase()} ${this.translate('merge2spaceTags').toLowerCase()}`;
      }
      if (this.isTemplate) {
        return this.translate('areYouSureYouWantToDeleteTheTemplate', {
          templateName: this.member.title,
        });
      }
      if (this.branchAutomation) {
        return this.translate('deleteAutomation');
      }
      if (this.isSection) {
        return `${this.translate('deleteSection')}`;
      }
      return this.translate('areYouSureYouWantToDelete', {
        resourceTitle: this.member.name,
        resourceType: this.translate('tag').toLowerCase(),
      });
    },
  },
  created() {
    window.addEventListener('keydown', this.handleClose);
    window.addEventListener('keyup', this.keypressHandler);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleClose);
    window.removeEventListener('keyup', this.keypressHandler);
  },
  mounted() {
    if (this.projectsWithSameTags?.length > 1) {
      this.lastMergeProject = this.projectsWithSameTags[this.projectsWithSameTags.length - 1];
    }
    if (this.$refs.doubleCheckPopup) {
      this.$refs.doubleCheckPopup.focus();
    }
  },
  methods: {
    updateCurrentUserRole(role, user) {
      const userToChange = {
        ...user,
        role,
      };
      this.updateUserInTeamMembers(userToChange);
    },
    updateUserInTeamMembers(updatedUser) {
      const key = updatedUser.id;
      const newMembers = { ...(this.$store.getters.getTeamMembers || {}) };
      newMembers[key] = updatedUser;
      const teamMembersArray = Object.values(newMembers);
      this.$store.dispatch('setTeamMembers', teamMembersArray);
      EventBus.$emit('teamMembersUpdated');
    },
    keypressHandler(e) {
      if (e.code === 'Enter') {
        this.deleteMember();
      }
      if (e.code === 'Escape') {
        if (this.discardComment && !this.escFullyPressed) {
          this.escFullyPressed = true;
          return;
        }
        this.closePopupHandler();
      }
    },
    closePopupHandler($event) {
      if ($event?.type === 'click') $event.stopPropagation();
      this.$emit('popupCancelled', this.member);
      if (this.spaceAccess) this.$emit('setPreviousValue');

      this.closePopup();
      if (this.isNewTag) {
        this.$emit('deleteUnmergedTag');
      }
      if (this.userData) {
        this.userData.checked = true;
      }
    },
    handleClose(e) {
      if (e.keyCode === 27 || e.key === 'Escape') {
        this.escFullyPressed = false;
        this.closePopupHandler();
        e.stopPropagation();
      }
    },
    updateTeamMember(role) {
      this.updateCurrentUserRole(role, this.member);
      this.$store
        .dispatch('updateTeamMember', {
          userId: this.member.id,
          payload: role,
        })
        .then(() => {
          this.updateCurrentUserRole('admin', this.currentUser);
        });
    },
    deleteTeamMember() {
      const value = this.member.id;
      const memberIndex = this.membersArray.findIndex(
        (memberToDelete) => memberToDelete.id === value
      );
      const member = this.membersArray[memberIndex];

      this.$store.dispatch('removeWorkspaceFromRecentWorkspaces', this.getTeamID);
      this.membersArray.splice(memberIndex, 1);
      this.$store
        .dispatch('deleteTeamMember', this.member.id)
        .then(() => {
          EventBus.$emit('member-removed', this.member.id);
          this.$store.dispatch('localRemoveTeamMember', this.member.id);
          if (this.me) {
            this.$store.dispatch('locaRemoveUserFromWorkspace', this.getTeamID);
            this.resetState();
            this.$router.push({ name: this.$constants.routeNames.workspaces });
          }
        })
        .catch(() => {
          this.membersArray.splice(memberIndex, 0, member);
          this.$store.dispatch('addUiNotification', {
            message: 'Action failed',
            status: this.$constants.uiNotificationStatuses.error,
          });
        });
    },
    revokeInvite() {
      const value = this.member.invitee_email;
      const inviteIndex = this.pendingInvites.findIndex(
        (inviteToRevoke) => inviteToRevoke.invitee_email === value
      );
      const invite = this.pendingInvites[inviteIndex];
      this.pendingInvites.splice(inviteIndex, 1);
      this.$store
        .dispatch('deleteTeamInvites', [this.member])
        .then(() => {
          EventBus.$emit('member-removed', this.member.invitee_user_id);
        })
        .catch(() => {
          this.pendingInvites.splice(inviteIndex, 0, invite);
          this.$store.dispatch('addUiNotification', {
            message: 'Action failed',
            status: this.$constants.uiNotificationStatuses.error,
          });
        });
    },
    closePopup() {
      this.$emit('closeDoubleCheckPopup');
    },
    handleSpaceAccess() {
      this.project.is_private = this.spaceType === 'private';
      this.$store.dispatch('updateProject', {
        projectData: { is_private: this.spaceType === 'private' },
        projectId: this.project.id,
        skipCheck: true,
      });
      this.closePopup();
    },

    deleteMember($event) {
      if ($event?.type === 'click') $event.stopPropagation();

      if (this.spaceAccess) {
        this.handleSpaceAccess();
        return;
      }
      if (this.discardComment) {
        this.$emit('deleteCommentConfirmed');
        this.closePopup();
        return;
      }
      if (this.me && this.project) {
        this.$emit('leaveSpaceConfirmed');
        this.closePopup();
        return;
      }
      if (this.isInvite) {
        this.revokeInvite();
        this.closePopup();
        return;
      }
      if (this.isOwner) {
        this.updateTeamMember('owner');
        this.closePopup();
        return;
      }
      if (this.member.role) {
        this.deleteTeamMember();
        this.closePopup();
        return;
      }
      if (this.isNewTag) {
        this.$emit('mergeTags');
        this.closePopup();
      }
      if (this.isTag) {
        this.$emit('deleteTagConfirmed');
        this.closePopup();
      }
      if (this.isTemplate) {
        this.$emit('deleteTemplateConfirmed');
        this.closePopup();
      }
      if (this.branchAutomation) {
        this.$emit('deleteBranchWorkflow', this.branchAutomation);
        this.closePopup();
      }
      if (this.isSection) {
        this.$emit('deleteSectionConfirmed');
        this.closePopup();
      }
    },
    resetState() {
      this.$store.dispatch('resetState');
      this.$store.dispatch('resetPageState');
      this.$store.dispatch('resetBoardState');
      this.$store.dispatch('resetOnboardingState');
    },
  },
};
</script>

<style lang="scss">
.double-check-modal {
  .popup-header {
    @apply rounded-t-[11px];
  }
  .popup-window {
    border: none;
    border-radius: 12px;
  }
  .double-check-popup {
    width: 400px;
    @apply border border-solid border-important-border;
    .check-popup-header {
      display: flex;
      padding: 24px 24px 0px 24px;
      .popup-header-title {
        font-style: normal;
        font-weight: 500;
        font-size: 18px;
        line-height: 28px;
        margin-right: 12px;
        @apply text-text-default;
      }
    }
    .delete-member-logo {
      width: 48px;
      height: 48px;
      background-color: #fee4e2;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      border: 8px #fef3f2 solid;
      margin-left: 20px;
      margin-top: 20px;
      margin-bottom: 16px;
    }
    .delete-members-title {
      margin-left: 24px;
      font-weight: 500;
      font-size: 18px;
      line-height: 28px;
      color: $gray900;
    }
    .member-info {
      display: flex;
      align-items: center;
      margin-left: 24px;
      margin-top: 16px;
      .avatar-image {
        margin-left: 0;
        margin-right: 10px;
        width: 32px;
        height: 32px;
        .user-avatar-main .avatar.small {
          width: 32px;
          height: 32px;
        }
      }
    }
    .action-warning {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      margin-left: 24px;
      margin-right: 24px;
      margin-top: 8px;
      @apply text-text-default;
      .merge-warning {
        box-sizing: border-box;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        height: 94px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        @apply text-text-default;
      }
    }
    .member-name {
      margin-left: 0;
      @apply text-text-default;
    }
    .member-email {
      margin-left: 0;
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      @apply text-text-default;
    }
  }
  .buttons-wrap {
    margin: 24px 24px 24px 24px;
    display: flex;

    .members-form-buttons-right {
      display: flex;
      margin-left: auto;

      .button-wrap {
        &.large-fixed {
          width: 173px;
          height: 32px;
          @apply rounded;
          .button-label {
            @apply text-sm
            font-normal;
          }
        }

        &.cancel-button {
          box-shadow: 0px 1px 2px 0px #1018280d;
          @apply border border-important-border mr-2;
        }

        &.delete-member-button {
          box-shadow: 0px 1px 2px 0px #1018280d;
          background-color: $error600;
          @apply hover:bg-red-700;
          em {
            order: 2;
            margin-left: 10px;
            font-size: 24px;
          }
        }

        &.discard-comment-button {
          box-shadow: 0px 1px 2px 0px #1018280d;
          @apply bg-attention-bg text-attention-text hover:bg-attention-bgHover;
          em {
            order: 2;
            margin-left: 10px;
            font-size: 24px;
          }
        }
      }
    }
  }
  .email-subtitle {
    color: $gray500;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 12px;
    margin-left: 24px;
  }
}
</style>
